import { CUSTOM_NOTICE_REJECTED_RECORD } from '@/constants';
import { fetchUpsertCustomNoticeSubscription } from '@/services';
import { useAppStore, useUserStore } from '@/store';
import type { CustomNoticeScene } from '@/types';
import { commonUtils } from '../common';
import dayjs from '../dayjs';
import { storage } from '../storage';

/** 订阅请求中标识，防止连点重复触发 */
let isRequesting = false;

/** 当天是否已拒绝过授权，拒绝后全部场景当天不再触发 */
const isRejectedToday = () => {
  const date = storage.get(CUSTOM_NOTICE_REJECTED_RECORD);
  if (!date) return false;
  if (commonUtils.date.isSameDay(date)) return true;
  // 非当天记录已失效，清理掉
  storage.remove(CUSTOM_NOTICE_REJECTED_RECORD);
  return false;
};

/** 记录拒绝授权的时间 */
const setRejected = () => {
  storage.set(CUSTOM_NOTICE_REJECTED_RECORD, dayjs().format('YYYY-MM-DD HH:mm:ss'));
};

/** 唤起订阅消息授权弹窗，返回各模版的授权结果，调用失败返回 null */
const requestSubscribe = (tmplIds: string[]) => {
  return new Promise<AnyObject | null>((reslove) => {
    // #ifdef MP-WEIXIN
    uni.requestSubscribeMessage({
      tmplIds,
      success(res: AnyObject) {
        console.log(`requestSubscribeMessage success`, res);
        reslove(res);
      },
      fail: (err) => {
        console.log(`requestSubscribeMessage fail`, err);
        reslove(null);
      },
    });
    // #endif

    // #ifndef MP-WEIXIN
    reslove(null);
    // #endif
  });
};

export default {
  /**
   * 触发自定义订阅通知授权
   * @param scene - 触发场景
   */
  trigger: (scene: CustomNoticeScene) => {
    // #ifdef MP-WEIXIN
    if (isRequesting) return;

    const appStore = useAppStore();
    const userStore = useUserStore();

    if (!userStore.isLogin) return;
    // 当天已拒绝过，不再打扰
    if (isRejectedToday()) return;

    // 该场景未开启或无待授权模版时，缓存为空
    const templates = appStore.customNoticeSceneTemplates[scene];
    if (!templates?.length) return;

    isRequesting = true;
    requestSubscribe(templates.map((item) => item.templateId))
      .then(async (res) => {
        // 调用失败（如订阅消息主开关关闭）不计入拒绝，后续仍可再次提示
        if (!res) return;

        // 只回传本次授权成功的模版
        const acceptedIds = templates
          .filter((item) => res[item.templateId] === 'accept')
          .map((item) => item.id);
        // 全部模版都未授权，视为拒绝
        if (!acceptedIds.length) {
          setRejected();
          return;
        }

        const isSuccess = await fetchUpsertCustomNoticeSubscription(acceptedIds);
        if (isSuccess) appStore.removeCustomNoticeTemplates(acceptedIds);
      })
      .catch((error) => {
        console.error('自定义订阅通知授权失败', error);
      })
      .finally(() => {
        isRequesting = false;
      });
    // #endif
  },
};
